import React, { useEffect, useMemo, useState } from "react";
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { GlobalState } from "context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { AuthState } from "context/AuthContext";
import { roleAccess } from "../DiscountMasterList";
import { Discount_Master_Brands, DISOUNT_MASTER_CHANNEL_OPTIONS } from "../data";

const initialFormData = {
    channel: "",
    brand: "",
    approver_mail: [],
};

function ApprovalMAster() {
    const [formData, setFormData] = useState(initialFormData);
    const [approvalRows, setApprovalRows] = useState([]);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [emailInput, setEmailInput] = useState("");
    const [showEmailError, setShowEmailError] = useState(false);
    const [editingRow, setEditingRow] = useState(null);

    const { user } = AuthState();
    const roleData = roleAccess(user);
    console.log("Sxnaksdjbjkasvbc", roleData)
    const role = ["VIEWER", "EDITOR"].includes(roleData.roles);
    const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();

    const channelOptions = useMemo(
        () =>
            DISOUNT_MASTER_CHANNEL_OPTIONS.filter((item) =>
                roleData?.channelList?.includes(item.key)
            ),
        [roleData]
    );

    const brandOptions = useMemo(
        () =>
            Discount_Master_Brands.filter((item) =>
                roleData?.brandList?.includes(item.key)
            ),
        [roleData]
    );
    const brandOptionKeys = useMemo(() => brandOptions.map((item) => item.key), [brandOptions]);

    const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email);
    const invalidEmailExists = formData.approver_mail.some((mail) => !isValidEmail(mail));

    const handleGetApprovalData = async () => {
        try {
            setLoadingOpen(true);
            let body = {
                    brand: roleData?.brandList,
                    channel: roleData?.channelList,
                };
            pythonVmInstance
                        .post("/discount-master/approvals", body)
            .then((res) => {
                 setApprovalRows(res?.data || []);
                 setLoadingOpen(false);
            })
        } catch (err) {
            const message = err?.response?.data?.message;
            setSnackBarOpen(true);
            setSnackBar({
                ...snackBar,
                message: message || "Unable to fetch approval master data",
                severity: "error",
                anchorOrigin: { horizontal: "center", vertical: "top" },
            });
        } finally {
            setLoadingOpen(false);
        }
    };

    useEffect(() => {
        handleGetApprovalData();
    }, []);

    const handleChange = (field) => (e) => {
        setFormData((prev) => ({
            ...prev,
            [field]: e.target.value,
        }));
    };

    const handleOpenDialog = () => {
        setFormData(initialFormData);
        setEmailInput("");
        setShowEmailError(false);
        setEditingRow(null);
        setOpenAddDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenAddDialog(false);
        setEditingRow(null);
    };

    const toList = (value) => {
        if (Array.isArray(value)) return value.filter(Boolean).map((item) => String(item).trim());
        if (!value || value === "-") return [];
        return String(value)
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);
    };

    const handleOpenEditDialog = (row) => {
        const channel = row?.channel || row?.Channel || "";
        const brands = toList(row?.brand || row?.Brand || []);
        const emails = toList(row?.approver_email || row?.approver_mail || row?.approver_mail || []).map(
            (item) => item.toLowerCase()
        );

        setFormData({
            ...row,
            channel,
            brand: brands,
            approver_mail: emails,
        });
        setEmailInput("");
        setShowEmailError(false);
        setEditingRow(row);
        setOpenAddDialog(true);
    };

    const addEmailChips = (rawValue) => {
        const parsed = String(rawValue || "")
            .split(/[,\s;]+/)
            .map((item) => item.trim().toLowerCase())
            .filter(Boolean);

        if (!parsed.length) return;

        setFormData((prev) => {
            const merged = [...prev.approver_mail, ...parsed];
            const unique = [...new Set(merged)];
            return { ...prev, approver_mail: unique };
        });
    };

    const handleSave = async () => {
        if (emailInput.trim()) {
            addEmailChips(emailInput);
            setEmailInput("");
        }

        const approverEmails = [
            ...new Set(
                [...formData.approver_mail, ...String(emailInput || "")
                    .split(/[,\s;]+/)
                    .map((item) => item.trim().toLowerCase())
                    .filter(Boolean)]
            ),
        ];

        const payload = {
            channel: formData.channel.trim(),
            brand: formData.brand,
            approver_email: approverEmails,
            email: user?.email,
        };
        const approvalMasterId = editingRow?.id || editingRow?._id;
        if (approvalMasterId !== undefined && approvalMasterId !== null) {
            payload.id = approvalMasterId;
        }

        if (!payload.channel || !payload.brand.length || !payload.approver_email.length) {
            setSnackBarOpen(true);
            setSnackBar({
                ...snackBar,
                message: "Please fill all required fields",
                severity: "warning",
                anchorOrigin: { horizontal: "center", vertical: "top" },
            });
            return;
        }
        if (!payload.approver_email.every((mail) => isValidEmail(mail))) {
            setShowEmailError(true);
            setSnackBarOpen(true);
            setSnackBar({
                ...snackBar,
                message: "Please enter valid approver emails",
                severity: "warning",
                anchorOrigin: { horizontal: "center", vertical: "top" },
            });
            return;
        }
        const alreadyExists = approvalRows.some(
            (row) => row?.brand === payload.brand && row?.channel === payload.channel
        );

        if (alreadyExists) {
            setSnackBarOpen(true);
            setSnackBar({
                ...snackBar,
                message: "Approval master data with same channel and brand already exists",
                severity: "warning",
                anchorOrigin: { horizontal: "center", vertical: "top" },
            });
            return; // ✅ This exits your function
        }

        try {
            setLoadingOpen(true);
            if (editingRow) {
                await pythonVmInstance.post("/discount-master/approval-master", payload, {
                    headers: { "Content-Type": "application/json" },
                });
            } else {
                await pythonVmInstance.post("/discount-master/approval-master", payload, {
                    headers: { "Content-Type": "application/json" },
                });
            }

            setSnackBarOpen(true);
            setSnackBar({
                ...snackBar,
                message: editingRow
                    ? "Approval master data updated successfully"
                    : "Approval master data saved successfully",
                severity: "success",
                anchorOrigin: { horizontal: "center", vertical: "top" },
            });

            setOpenAddDialog(false);
            setFormData(initialFormData);
            setEmailInput("");
            setShowEmailError(false);
            setEditingRow(null);
            handleGetApprovalData();
            setLoadingOpen(false);
        } catch (err) {
            const message = err?.response?.data?.message;
            setSnackBarOpen(true);
            setSnackBar({
                ...snackBar,
                message: message || "Unable to save approval master data",
                severity: "error",
                anchorOrigin: { horizontal: "center", vertical: "top" },
            });
            setLoadingOpen(false);
        }
    };

    console.log("dsfghjopkhgf", approvalRows)

    return (
        <Stack width="98%" p={2} maxHeight={500} spacing={2}>
            <Box display="flex" justifyContent="flex-end" alignItems="center">

                <Button variant="contained" onClick={handleOpenDialog} disabled={role}>
                    Add Approver
                </Button>
            </Box>

            <TableContainer component={Paper} sx={{ maxHeight: 380 }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700, minWidth: "150px" }}>Channel</TableCell>
                            <TableCell sx={{ fontWeight: 700, minWidth: "150px" }}>Brand</TableCell>
                            <TableCell sx={{ fontWeight: 700,minWidth: "150px" }}>Publisher Emails</TableCell>
                            <TableCell sx={{ fontWeight: 700, width: 120, minWidth: "100px" }}>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {approvalRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    No approval master data found
                                </TableCell>
                            </TableRow>
                        ) : (
                            approvalRows.map((row, idx) => {
                                const channel = row?.channel || row?.Channel || "-";
                                const rawBrand = row?.brand || row?.Brand || [];
                                const rawApproverEmail =
                                    row?.approver_email || row?.approver_mail || [];
                                const brand = Array.isArray(rawBrand) ? rawBrand : toList(rawBrand);
                                const approver_mail = Array.isArray(rawApproverEmail)
                                    ? rawApproverEmail
                                    : toList(rawApproverEmail);

                                return (
                                    <TableRow key={`${channel}-${idx}`} hover>
                                        <TableCell sx={{minWidth: "150px"}}>{channel}</TableCell>
                                        <TableCell sx={{minWidth: "150px"}}>
                                            <Chip label={brand} size="small" />
                                        </TableCell>
                                        <TableCell sx={{minWidth: "150px"}}>
                                            <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                                {approver_mail.length ? (
                                                    approver_mail.map((item, eIdx) => (
                                                        <Chip key={`${item}-${eIdx}`} label={item} size="small" variant="outlined" />
                                                    ))
                                                ) : (
                                                    <Typography variant="caption">-</Typography>
                                                )}
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={{minWidth: "100px"}}>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<EditIcon fontSize="small" />}
                                                disabled={role}
                                                onClick={() => handleOpenEditDialog(row)}
                                            >
                                                Edit
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={openAddDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                <DialogTitle>{editingRow ? "Edit Approval Master" : "Add Approval Master"}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} mt={1}>
                        <TextField
                            select
                            label="Channel"
                            size="small"
                            disabled={editingRow}
                            value={formData.channel}
                            onChange={handleChange("channel")}
                        >
                            {channelOptions.map((item) => (
                                <MenuItem key={item.key} value={item.key}>
                                    {item.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label="Brand"
                            disabled={editingRow}
                            size="small"
                            value={formData.brand}
                            onChange={handleChange("brand")}
                        >
                            {brandOptions.map((item) => (
                                <MenuItem key={item.key} value={item.key}>
                                    {item.name}
                                </MenuItem>
                            ))}
                        </TextField>



                        <Autocomplete
                            multiple
                            freeSolo
                            options={[]}
                            value={formData.approver_mail}
                            inputValue={emailInput}
                            onInputChange={(_, value) => setEmailInput(value)}
                            onBlur={() => {
                                if (emailInput.trim()) {
                                    addEmailChips(emailInput);
                                    setEmailInput("");
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === "," || e.key === "Tab") {
                                    e.preventDefault();
                                    if (emailInput.trim()) {
                                        addEmailChips(emailInput);
                                        setEmailInput("");
                                    }
                                }
                            }}
                            onChange={(_, value) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    approver_mail: value
                                        .map((item) => String(item).trim().toLowerCase())
                                        .filter(Boolean),
                                }))
                            }
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        {...getTagProps({ index })}
                                        key={`${option}-${index}`}
                                        label={option}
                                        size="small"
                                        variant="outlined"
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Approver Email"
                                    size="small"
                                    helperText="Type email and press Enter/Comma/Tab"
                                    error={showEmailError && invalidEmailExists}
                                />
                            )}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}

export default ApprovalMAster;
